/**
 * Shop categories — admin managed, single source of truth for /shop and the
 * external Faith Works Bookshop site.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Save, Trash2 } from 'lucide-react';
import { MediaUpload } from '@/components/MediaUpload';

const BLANK = { label: '', description: '', image_url: '', hidden: false };

const Row = ({ row, onMove, first, last }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(row);
  React.useEffect(() => setForm(row), [row]);
  const dirty = JSON.stringify(form) !== JSON.stringify(row);
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['shop-admin-categories'] });
    queryClient.invalidateQueries({ queryKey: ['shop-admin-products'] });
    queryClient.invalidateQueries({ queryKey: ['shop-admin-settings'] });
    queryClient.invalidateQueries({ queryKey: ['shop-admin-dashboard'] });
  };

  const save = useMutation({
    mutationFn: async () => (await api.put(`/shop/admin/categories/${row.id}`, {
      label: form.label, description: form.description,
      image_url: form.image_url, hidden: !!form.hidden,
    })).data,
    onSuccess: () => { toast.success('Category saved'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/shop/admin/categories/${row.id}`)).data,
    onSuccess: () => { toast.success('Category removed'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3"
         data-testid={`shop-category-row-${row.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-900">{row.label}</span>
        <Badge variant="outline" className="text-[10px] font-mono">{row.key}</Badge>
        <Badge variant="secondary" className="text-[10px]">
          {row.published} live / {row.products}
        </Badge>
        {row.hidden && (
          <Badge variant="outline" className="text-[10px] text-slate-500">hidden</Badge>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button size="icon" variant="ghost" disabled={first}
                  data-testid={`shop-category-up-${row.key}`}
                  onClick={() => onMove(row.id, -1)}>
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" disabled={last}
                  data-testid={`shop-category-down-${row.key}`}
                  onClick={() => onMove(row.id, 1)}>
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button size="icon" variant="ghost" data-testid={`shop-category-delete-${row.key}`}
                  onClick={() => remove.mutate()}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Name shown to shoppers</Label>
          <Input value={form.label} data-testid={`shop-category-label-${row.key}`}
                 onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Short description</Label>
          <Input value={form.description || ''}
                 data-testid={`shop-category-desc-${row.key}`}
                 onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <MediaUpload value={form.image_url || ''} onChange={(url) => setForm({ ...form, image_url: url })} purpose="cover" label="Category image" testId={`shop-category-image-${row.key}`} previewClassName="h-16" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch checked={!form.hidden} data-testid={`shop-category-visible-${row.key}`}
                  onCheckedChange={(v) => setForm({ ...form, hidden: !v })} />
          <span className="text-xs text-slate-600 inline-flex items-center gap-1.5">
            {form.hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {form.hidden ? 'Hidden everywhere' : 'Shown in the shop and on the website'}
          </span>
        </div>
        <Button size="sm" className="ml-auto" disabled={!dirty || save.isPending}
                data-testid={`shop-category-save-${row.key}`}
                onClick={() => save.mutate()}>
          <Save className="h-3.5 w-3.5 mr-1.5" /> Save
        </Button>
      </div>
    </div>
  );
};

export const ShopCategoriesTab = () => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(BLANK);
  const { data, isLoading } = useQuery({
    queryKey: ['shop-admin-categories'],
    queryFn: async () => (await api.get('/shop/admin/categories')).data,
  });

  const add = useMutation({
    mutationFn: async () => (await api.post('/shop/admin/categories', draft)).data,
    onSuccess: () => {
      toast.success('Category added');
      setDraft(BLANK);
      queryClient.invalidateQueries({ queryKey: ['shop-admin-categories'] });
      queryClient.invalidateQueries({ queryKey: ['shop-admin-settings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reorder = useMutation({
    mutationFn: async (ids) => (await api.post('/shop/admin/categories/reorder', { ids })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['shop-admin-categories'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full" />;
  const rows = data?.categories || [];

  const move = (id, delta) => {
    const ids = rows.map((r) => r.id);
    const from = ids.indexOf(id);
    const to = from + delta;
    if (to < 0 || to >= ids.length) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    reorder.mutate(ids);
  };

  return (
    <div className="space-y-5" data-testid="shop-categories">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Categories</CardTitle>
          <CardDescription>
            This order is the order shoppers see — in the app shop and on the
            Faith Works Bookshop website. Hiding a category takes it off both at once.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {rows.map((row, i) => (
            <Row key={row.id} row={row} onMove={move}
                 first={i === 0} last={i === rows.length - 1} />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Add a category</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name *</Label>
              <Input value={draft.label} data-testid="shop-new-category-label"
                     placeholder="Children's Bibles"
                     onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Short description</Label>
              <Input value={draft.description} data-testid="shop-new-category-desc"
                     onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <MediaUpload value={draft.image_url} onChange={(url) => setDraft({ ...draft, image_url: url })} purpose="cover" label="Category image" testId="shop-new-category-image" previewClassName="h-16" />
            </div>
          </div>
          <Button size="sm" data-testid="shop-add-category-btn"
                  disabled={draft.label.trim().length < 2 || add.isPending}
                  onClick={() => add.mutate()}>
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add category
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopCategoriesTab;
